import { useEffect, useState } from 'react'

import classnames from 'classnames'

import {
  Container,
  Label,
  Labels,
  LoadingIndicator,
  LoadingIndicatorContainer,
  PlayButton,
  Pulse,
  TextCycle,
  VisualizerWrapper,
} from './styles'

import {
  IconHeadphonesSolid,
  IconPlaySolid,
  IconVolume2Outline,
  IconVolumeXOutline,
} from 'components/icons'
import { Visualizer } from 'components/visualiser'
import { useAppContext } from 'context/AppContext'
import { useSyncServerContext } from 'context/SyncServerContext'
import { useLocaleTranslation } from 'hooks/useLocaleTranslation'
import { useSingleTrackAudioPlayer } from 'hooks/useSingleTrackAudioPlayer'
import type { UseAudioPlayerProps } from 'hooks/useSingleTrackAudioPlayer/types'
import type { EventTrack } from 'types/player'
import { getConfigData } from 'utils/helpers/config'
import { disableVisualizer } from 'utils/helpers/debug'

type SingleTrackAudioPlayerProps = {
  /** Called when all tracks have been successfully loaded and the clock is synced */
  onTracksLoaded: (tracks: EventTrack[]) => void
  /** Called when an error occurs while loading tracks */
  onTracksLoadingError: (e: any) => void
} & Omit<UseAudioPlayerProps, 'track' | 'startTime' | 'socket'>

export const SingleTrackAudioPlayer = ({
  onTracksLoaded,
  onPlay,
  onActiveFinishedAtInit,
  onActiveFinished,
  onMuteToggle,
  onError,
  onTracksLoadingError,
  onPlayNewTrack,
}: SingleTrackAudioPlayerProps) => {
  const audioConfig = getConfigData('audioConfig')

  const { _ } = useLocaleTranslation('Player')
  const { track, startTime, socket, clockSynced } = useSyncServerContext()
  const { tracks } = useAppContext()
  const [tracksLoaded, setTracksLoaded] = useState<boolean>(false)
  const [loaded, setLoaded] = useState<boolean>(false)
  const {
    audioRef,
    audioCtx,
    gainNode,
    sourceNode,
    handleMuteToggle,
    handlePlay,
    hasPlayed,
    isMuted,
    // when the player starts, it does so silently, only
    // fading in after 5 seconds.
    isWarmingUp,
  } = useSingleTrackAudioPlayer({
    startTime,
    track,
    socket: socket.current,
    onPlay,
    onPlayNewTrack,
    onActiveFinishedAtInit,
    onActiveFinished,
    onMuteToggle,
    onError,
  })

  useEffect(() => {
    if (tracks.length > 0) {
      const fetchTracks = async () => {
        const loadTrack = async (trackUrl: string) => {
          try {
            const response = await fetch(trackUrl)
            if (!response.ok) {
              throw new Error(`Failed to fetch track from ${trackUrl}`)
            }
          } catch (error) {
            console.error('Error loading track:', error)
            onTracksLoadingError?.(error)
            return null
          }
        }
        await Promise.all(
          tracks.map((_track: EventTrack) => loadTrack(_track.url)),
        )
        setTracksLoaded(true)
      }
      fetchTracks()
    }
  }, [tracks])

  useEffect(() => {
    if (tracksLoaded && clockSynced) {
      onTracksLoaded?.(tracks)
      setLoaded(true)
    }
  }, [tracksLoaded, clockSynced])

  const showVisualizer =
    audioConfig.showVisualizer && !disableVisualizer() && hasPlayed

  const processing = isWarmingUp || !loaded

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} crossOrigin="anonymous" />
      <Container>
        {showVisualizer && (
          <VisualizerWrapper $show={!isWarmingUp}>
            <Visualizer
              audioCtx={audioCtx}
              sourceNode={sourceNode}
              gainNode={gainNode}
              isMuted={isMuted}
            />
          </VisualizerWrapper>
        )}
        <LoadingIndicatorContainer>
          <LoadingIndicator className={classnames({ show: processing })} />
          <IconHeadphonesSolid
            size={6}
            className={classnames({ show: loaded })}
          />
          <Pulse
            className={classnames({
              show: loaded,
            })}
          />
          <PlayButton
            className={classnames({ show: loaded || hasPlayed })}
            onClick={hasPlayed ? handleMuteToggle : handlePlay}
          >
            <IconPlaySolid
              size={6}
              className={classnames({
                show: loaded && !hasPlayed && !isWarmingUp,
              })}
            />
            <IconVolumeXOutline
              size={6}
              className={classnames({ show: hasPlayed && isMuted })}
            />
            <IconVolume2Outline
              size={6}
              className={classnames({ show: hasPlayed && !isMuted })}
            />
          </PlayButton>
        </LoadingIndicatorContainer>

        <Labels>
          <TextCycle className={classnames({ show: !loaded })}>
            <Label>{_('loading')}</Label>
            <Label>{_('headphones')}</Label>
          </TextCycle>

          <Label
            className={classnames({
              show: loaded && !hasPlayed && !isWarmingUp,
            })}
          >
            {_('headphones')}
          </Label>
          <Label
            className={classnames({
              show: hasPlayed && isMuted && !isWarmingUp,
            })}
          >
            {_('sound-off')}
          </Label>
          <Label
            className={classnames({
              show: hasPlayed && !isMuted && !isWarmingUp,
            })}
          >
            {_('sound-on')}
          </Label>
          <Label className={classnames({ show: isWarmingUp })}>
            {_('resync')}
          </Label>
        </Labels>
      </Container>
    </>
  )
}
